import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Shield, Users } from "lucide-react";
import { Button } from "./ui/button";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { getWithoutTokenAPI } from "../util/Api";
import toast, { Toaster } from "react-hot-toast";
import { useSelector } from "react-redux";
import Skeleton from "@mui/material/Skeleton";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

// ⭐ AOS Import
import AOS from "aos";
import "aos/dist/aos.css";

export function HeroSection({ onNavigate }) {
  const data = useSelector((state) => state.user);
  const token = data?.userId?.accessToken;
  const [loading, setLoading] = useState(false);
  const [header, setHeader] = useState(null);


  const { t, i18n } = useTranslation();

  const heading =
    i18n.language === "en"
      ? header?.heading?.en
      : header?.heading?.ta;

  const subHeading =
    i18n.language === "en"
      ? header?.subHeading?.en
      : header?.subHeading?.ta || header?.subHeading?.en ||
      "Join thousands of happy couples who found their soulmate through our trusted matrimony platform.";


  const navigate = useNavigate();
  AOS.init({
    duration: 1000,          // Smooth globally
    offset: 100,             // Animate after scrolling a bit
    easing: "ease-in-out",
    delay: 50,              // Slight delay
    once: true,
  });


  useEffect(() => {
    const fetchHeader = async () => {
      setLoading(true);
      try {
        const response = await getWithoutTokenAPI(`/header/get_all`);

        if (response?.statusCode === 200 && response.data?.length > 0) {
          const headerData = response.data[0];

          const fixedHeader = {
            ...headerData,
            browseProfiles: headerData.browseProfiles
              ? headerData.browseProfiles.replace(
                "s3.eu-north-1.amazonaws.com",
                "s3.ap-south-1.amazonaws.com"
              )
              : null,
          };

          setHeader(fixedHeader);
        }
      } catch (error) {
        console.error("Error fetching header:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeader();
  }, []);

  console.log("Headerdata:", header?.browseProfiles);

  if (loading) {
    return (
      <section className="relative bg-[#FFFBEB] py-20 px-4 sm:px-6 lg:px-8 animate-pulse">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* LEFT SIDE */}
            <div className="text-start lg:text-left space-y-6">
              <Skeleton variant="text" width="80%" height={60} sx={{ borderRadius: "8px" }} />
              <Skeleton variant="text" width="60%" height={40} sx={{ borderRadius: "6px" }} />
              <Skeleton variant="rectangular" width="90%" height={80} sx={{ borderRadius: "10px" }} />
              <div className="flex gap-4 mt-6">
                <Skeleton variant="rounded" width={180} height={48} sx={{ borderRadius: "8px" }} />
              </div>
              {/* Stats Skeleton */}
              <div className="flex justify-between gap-4 mt-12 w-[80%]">
                <div className="text-center space-y-2">
                  <Skeleton variant="text" width={80} height={30} />
                  <Skeleton variant="text" width={100} height={20} />
                </div>
                <div className="text-center space-y-2">
                  <Skeleton variant="text" width={80} height={30} />
                  <Skeleton variant="text" width={100} height={20} />
                </div>
                <div className="text-center space-y-2">
                  <Skeleton variant="text" width={80} height={30} />
                  <Skeleton variant="text" width={100} height={20} />
                </div>
              </div>
            </div>
            {/* RIGHT SIDE */}
            <div className="relative flex justify-center lg:justify-end">
              <Skeleton
                variant="rounded"
                width="100%"
                height={400}
                className="max-w-md lg:max-w-lg"
                sx={{ borderRadius: "16px" }}
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      <Helmet>
        <title>Aishwarya matrimony - Home</title>
        <meta name="description" content="This is my page description." />
        <meta name="keywords" content="React, Helmet, SEO" />
      </Helmet>

      <section
        className="relative bg-[#FFFBEB] py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-1 items-center">

            {/* LEFT SIDE */}
            <div className="text-start lg:text-left">
              <h1
                className="text-4xl md:text-5xl lg:text-6xl mb-6 text-primary"
                data-aos="fade-right"
              >
                <span className="text-red-500 block">{heading}</span>
              </h1>

              <p
                className="text-lg mb-8 max-w-lg mx-auto lg:mx-0"
                data-aos="fade-right"
                data-aos-delay="200"
              >
                {subHeading}
              </p>

              <div
                className="flex flex-col sm:flex-row gap-4 justify-right lg:justify-end mb-12"
                data-aos="zoom-in"
                data-aos-delay="400"
              >
                {!token ? (
                  <Button
                    size="lg"
                    className="text-white cursor-pointer"
                    style={{ background: 'linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))', paddingTop: '3px' }}
                    onClick={() => navigate("/login")}
                  >
                    {header?.buttons?.register || t("Register For Free")}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    variant="default"
                    className="text-white cursor-pointer"
                    style={{ background: 'linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))', paddingTop: '3px' }}
                    onClick={() => navigate("/browse")}
                  >
                    {header?.buttons?.browseProfiles || t("Browse Profiles")}
                  </Button>
                )}
              </div>

              {/* STATS */}
              <div
                className="flex flex-wrap justify-evenly md:justify-between gap-2"
                style={{
                  width: "80%",
                  display: "flex",
                  justifyContent: "space-between",
                }}
                data-aos="fade-up"
                data-aos-delay="600"
              >
                <div className="text-center">
                  <div className="text-2xl text-primary">
                    {header?.stats?.activeProfiles || "50,000+"}
                  </div>
                  <div className="text-sm text-muted-foreground"
                    style={{ textAlign: 'f' }}
                  >
                    {t("Active Profiles")}
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-2xl text-primary">
                    {header?.stats?.successStories || "10,000+"}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t("Success Stories")}
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-2xl text-primary">
                    {header?.stats?.matchRate || "95%"}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t("Match Rate")}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT IMAGE */}
            <div className="relative flex justify-center w-full" data-aos="fade-left">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-red-100 bg-white w-full max-w-2xl">
                <ImageWithFallback
                  src={header?.browseProfiles}
                  alt="Happy couple"
                  className="w-full h-[420px] sm:h-[520px] lg:h-[580px] object-contain p-2"
                />
              </div>

              <div
                className="absolute -top-6 -left-6 bg-white rounded-lg shadow-lg p-4 hidden md:block"
                data-aos="fade-up-right"
              >
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-green-500" />
                  <span className="text-sm">{t("100% Verified")}</span>
                </div>
              </div>

              <div
                className="absolute -bottom-6 -right-6 bg-white rounded-lg shadow-lg p-4 hidden md:block"
                data-aos="fade-down-left"
              >
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  <span className="text-sm">{t("100% Trusted")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FEATURES */}
        <div className="max-w-8xl mx-auto mt-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

            <div
              className="text-center p-6 bg-white rounded-xl shadow-sm"
              data-aos="zoom-in-up"
            >
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="mb-2">{t("Verified Profiles")}</h3>
              <p className="text-muted-foreground text-sm">
                {t("All profiles are manually verified for authenticity and safety")}
              </p>
            </div>

            <div
              className="text-center p-6 bg-white rounded-xl shadow-sm"
              data-aos="zoom-in-up"
              data-aos-delay="200"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="mb-2">{t("Smart Matching")}</h3>
              <p className="text-muted-foreground text-sm">
                {t("Advanced algorithm to find compatible matches based on preferences")}
              </p>
            </div>

            <div
              className="text-center p-6 bg-white rounded-xl shadow-sm"
              data-aos="zoom-in-up"
              data-aos-delay="400"
            >
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-green-500" />
              </div>
              <h3 className="mb-2">{t("Privacy First")}</h3>
              <p className="text-muted-foreground text-sm">
                {t("Your personal information is secure and protected at all times")}
              </p>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

